import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ReservationComponents } from './booking/ReservationComponents';
import { toast } from 'sonner';
import { CheckCircle, Loader2, XCircle, Users, Mail, Phone, ShieldCheck, RefreshCw, CreditCard } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const MAX_POLLS = 12;

const readPending = () => {
  try { return JSON.parse(localStorage.getItem('pendingHoldBooking') || 'null'); } catch { return null; }
};

export const HoldBookingSuccess = ({ sessionId, bookingRef, onGoHome }) => {
  const [pending] = useState(readPending);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [polls, setPolls] = useState(0);
  const [retrying, setRetrying] = useState(false);

  // Resolve the order by (in priority order): URL order id → URL booking ref → last checkout stored locally
  const orderId = sessionId || pending?.sessionId || null;
  const ref = bookingRef || pending?.bookingId || null;

  useEffect(() => {
    if (!orderId && !ref) { setError('We could not find a payment to verify. Please check your email or call us.'); return; }
    let cancelled = false;
    const query = orderId ? `order_id=${encodeURIComponent(orderId)}` : `booking_id=${encodeURIComponent(ref)}`;
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/payments/status?${query}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Failed to verify payment');
        if (cancelled) return;
        setStatus(data);
        if (data.paid) localStorage.removeItem('pendingHoldBooking');
        else if (!data.failed && polls < MAX_POLLS) setTimeout(() => setPolls((p) => p + 1), 4000);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [orderId, ref, polls]);

  const retry = async () => {
    setRetrying(true);
    if (status?.type === 'UMRAH_BALANCE' && status?.umrah?.balance_url) { window.location.href = status.umrah.balance_url; return; }
    try {
      const res = await fetch(`${API_URL}/api/umrah/payment/retry`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_id: status?.booking_id || ref, origin_url: window.location.origin })
      });
      const data = await res.json();
      if (!res.ok || !data.checkout_url) throw new Error(data.detail || 'Unable to restart payment');
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.message);
      setRetrying(false);
    }
  };

  const paid = status?.paid;
  const failed = status?.failed;
  const waiting = !status && !error;
  const processing = status && !paid && !failed;
  const timedOut = processing && polls >= MAX_POLLS;
  const pkg = pending?.package;
  const displayRef = status?.booking_id || ref || '—';
  const isBalance = status?.type === 'UMRAH_BALANCE';
  const isDeposit = status?.umrah?.payment_plan === 'DEPOSIT' && !isBalance;
  const outstanding = paid && status?.umrah && status.umrah.balance_due > 0;
  const dueDate = status?.umrah?.balance_due_date ? new Date(status.umrah.balance_due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  const headerClass = paid ? 'bg-emerald-600' : failed ? 'bg-red-600' : error ? 'bg-slate-700' : 'bg-brand-600';
  const allReserved = !!status?.umrah?.reservation?.all_reserved;
  const title = paid ? (isBalance ? 'Balance Received — Booking Paid in Full' : allReserved ? (isDeposit ? 'Deposit Received — Umrah Package Reserved' : 'Umrah Package Confirmed') : isDeposit ? 'Deposit Received — Confirming Your Package' : 'Payment Received — Confirming Your Package')
    : failed ? 'Payment Didn\'t Go Through'
    : error ? 'Payment Verification Failed'
    : timedOut ? 'Payment Still Processing' : 'Confirming Your Payment…';
  const subtitle = paid ? (isBalance ? 'Thank you — nothing more to pay. Your travel documents will follow by email.' : isDeposit ? 'Deposit received. We are now confirming your flights and hotels with the suppliers — each shows below once confirmed. Your confirmation email includes a secure link to pay the balance online.' : 'Payment received. We are now confirming each component with the suppliers — see the status below. A receipt and confirmation have been emailed to you.')
    : failed ? `Your card was ${status.state === 'cancelled' ? 'not charged — the payment was cancelled' : 'declined'}${status.failure_reason ? ` (${status.failure_reason})` : ''}. You can try again with the same or a different card.`
    : error ? error
    : timedOut ? 'We\'ll email you as soon as the bank confirms. Your booking reference is safe.'
    : 'This usually takes a few seconds. Please don\'t close this page.';

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10" data-testid="umrah-payment-return-page">
      <div className="max-w-2xl mx-auto space-y-6">
        <Card className="overflow-hidden border-0 shadow-2xl">
          <div className={`py-10 text-center text-white ${headerClass}`}>
            {waiting && <Loader2 className="h-14 w-14 mx-auto mb-4 animate-spin" />}
            {paid && <CheckCircle className="h-14 w-14 mx-auto mb-4" />}
            {(failed || error) && <XCircle className="h-14 w-14 mx-auto mb-4" />}
            {processing && !timedOut && <Loader2 className="h-14 w-14 mx-auto mb-4 animate-spin" />}
            {timedOut && <RefreshCw className="h-14 w-14 mx-auto mb-4" />}
            <h1 className="text-2xl sm:text-3xl font-bold" data-testid="umrah-payment-title">{title}</h1>
            <p className="mt-2 text-white/85 text-sm sm:text-base max-w-lg mx-auto" data-testid="umrah-payment-subtitle">{subtitle}</p>
          </div>

          <div className="bg-slate-900 p-8 text-center">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Booking reference</div>
            <div className="text-4xl sm:text-5xl font-black tracking-[0.15em] text-white select-all break-all" data-testid="umrah-payment-ref">{displayRef}</div>
            {status && (
              <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                <Badge className={`${paid ? 'bg-emerald-500' : failed ? 'bg-red-500' : 'bg-amber-500'} text-white px-3 py-1 text-sm`} data-testid="umrah-payment-status-badge">
                  {paid ? (isDeposit ? 'DEPOSIT PAID' : 'PAID') : (status.state || 'pending').toUpperCase()}
                </Badge>
                <span className="text-slate-300 text-sm" data-testid="umrah-payment-amount">
                  {status.currency === 'GBP' ? '£' : status.currency + ' '}{Number(status.amount || 0).toFixed(2)}
                </span>
              </div>
            )}
          </div>

          {paid && status?.umrah?.reservation && (
            <CardContent className="p-6 border-t border-slate-100" data-testid="umrah-reservation-status">
              <div className="font-bold text-slate-900 mb-2">Supplier confirmation</div>
              <ReservationComponents reservation={status.umrah.reservation} testIdPrefix="umrah-reservation" />
            </CardContent>
          )}

          {outstanding && (
            <CardContent className="p-6 bg-orange-50 border-t border-orange-100" data-testid="umrah-balance-outstanding">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-slate-800">
                  <div className="font-bold">Remaining balance: £{Number(status.umrah.balance_due).toLocaleString('en-GB')}</div>
                  <div>Due by {dueDate}. Pay securely online any time before then.</div>
                </div>
                {status.umrah.balance_url && (
                  <Button asChild className="h-12 px-6 bg-[#FF6600] hover:bg-[#e65c00] text-white font-bold" data-testid="umrah-pay-balance-link">
                    <a href={status.umrah.balance_url}><CreditCard className="h-5 w-5 mr-2" />Pay balance now</a>
                  </Button>
                )}
              </div>
            </CardContent>
          )}

          {(failed || timedOut) && status?.can_retry && (
            <CardContent className="p-6 bg-red-50 border-t border-red-100">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-red-800">
                  <div className="font-bold">Nothing has been charged.</div>
                  <div>Try again now, or call us and we'll take payment over the phone.</div>
                </div>
                <Button onClick={retry} disabled={retrying} className="h-12 px-6 bg-brand-600 hover:bg-brand-700 text-white font-bold" data-testid="umrah-retry-payment-btn">
                  {retrying ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <CreditCard className="h-5 w-5 mr-2" />}
                  {retrying ? 'Redirecting…' : 'Try payment again'}
                </Button>
              </div>
            </CardContent>
          )}
        </Card>

        {pkg && (
          <Card>
            <CardHeader>
              <CardTitle>Your Umrah Package</CardTitle>
              <CardDescription>Saved from your builder session</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-2 text-sm">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Users className="h-5 w-5 text-brand-600" />
                <div>
                  <div className="font-medium">Passengers</div>
                  <div className="text-slate-600">
                    {pkg.passengers?.adults || 0} adult{(pkg.passengers?.adults || 0) !== 1 ? 's' : ''}
                    {pkg.passengers?.children > 0 && `, ${pkg.passengers.children} children`}
                    {pkg.passengers?.infants > 0 && `, ${pkg.passengers.infants} infants`}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Mail className="h-5 w-5 text-brand-600" />
                <div>
                  <div className="font-medium">{paid ? 'Receipt sent to' : 'Receipt will be sent to'}</div>
                  <div className="text-slate-600 break-all">{status?.contact_email || pending?.contact?.email}</div>
                </div>
              </div>
              {pkg.hotels?.primary?.name && (
                <div className="sm:col-span-2 p-3 bg-slate-50 rounded-lg text-slate-700">
                  <span className="font-medium">Hotels:</span> {pkg.hotels.primary.name}{pkg.hotels.secondary?.name ? ` · ${pkg.hotels.secondary.name}` : ''}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-600">
          <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Secured by Revolut · ATOL protected</div>
          <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-brand-600" /> 01908 220000 · 01582 808050</div>
        </div>

        <Button onClick={onGoHome} variant={failed ? 'outline' : 'default'} className={`w-full h-12 font-semibold ${failed ? '' : 'bg-brand-600 hover:bg-brand-700 text-white'}`} data-testid="umrah-payment-home-btn">
          Back to Flight380
        </Button>
      </div>
    </div>
  );
};

export default HoldBookingSuccess;
